// ── Adapter HTTP do IListaVidasGateway ────────────────────────────
// Fala com /api/lista-vidas, a rota do NOSSO servidor (server/lista-vidas/).
// É ele quem guarda as chaves do Hub (api-bluehub) e lê/escreve as vidas; o
// navegador só manda a sessão do Supabase como Bearer e nunca vê chave do Hub.
//
// Sem timeout: aprovar/retomar escrevem no Hub dentro da requisição (até 3
// passadas, com lease) e podem levar mais de um minuto. Queda de rede vira
// `codigo: 'rede'` e a tela relê o estado em vez de assumir falha.
//
// Erros do servidor trazem `correlationId` (rastreio no Hub) e `retryAfterS`;
// ambos são repassados no `ListaVidasGatewayError`. No sucesso, o
// `correlationId` da escrita vem dentro de `data.respostaHub`.

import { getSupabase } from '@infra/supabase/client';
import { ListaVidasGatewayError } from '@modules/listaVidas';
import type {
  IListaVidasGateway, ListaVidasPapelInfo, ListaVidasEmpresas, ListaVidasVidas, ListaVidasVisaoGeral,
  VidaCompletaDTO, FiltroSolicitacoes, EventoSolicitacao, NovaSolicitacao,
  RespostaAplicacao, SolicitacaoDTO, ControleAdicionalDTO, BonificacaoEmpresa,
  SalvarAdicionalVidaInput, LiberarZenklubInput, SalvarBonificacaoInput,
} from '@modules/listaVidas';

const ROTA = '/api/lista-vidas';

type Acao =
  | 'meu-papel' | 'listar-empresas' | 'listar-vidas' | 'visao-geral' | 'obter-vida' | 'listar-solicitacoes'
  | 'obter-solicitacao' | 'criar' | 'aprovar' | 'retomar' | 'rejeitar' | 'cancelar'
  | 'salvar-adicional-vida' | 'liberar-zenklub' | 'salvar-bonificacao';

interface Corpo<T> {
  ok?: boolean;
  data?: T;
  codigo?: string;
  error?: string;
  erros?: unknown[];
  problemas?: unknown[];
  solicitacaoId?: string;
  statusAtual?: string;
  retentavel?: boolean;
  correlationId?: string;
  retryAfterS?: number;
}

export class ListaVidasApiClient implements IListaVidasGateway {
  private async chamar<T>(acao: Acao, params: Record<string, unknown> = {}): Promise<T> {
    const client = getSupabase();
    if (!client) throw new Error('Supabase não inicializado.');
    const { data } = await client.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Sessão inválida.');

    let response: Response;
    try {
      response = await fetch(ROTA, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao, params }),
      });
    } catch {
      throw new ListaVidasGatewayError({
        codigo: 'rede', status: 0, mensagem: 'Sem resposta do servidor.',
      });
    }

    // Texto antes de parsear: com o backend fora do ar quem responde é o
    // proxy, em texto puro.
    const texto = await response.text();
    let corpo: Corpo<T>;
    try {
      corpo = JSON.parse(texto);
    } catch {
      throw new ListaVidasGatewayError({
        codigo: response.ok ? 'resposta_invalida' : 'servidor_indisponivel',
        status: response.status,
        mensagem: `HTTP ${response.status} de ${ROTA}`,
      });
    }

    if (!response.ok) {
      throw new ListaVidasGatewayError({
        codigo: corpo.codigo ?? 'desconhecido',
        status: response.status,
        mensagem: corpo.error || `Falha ao falar com a Lista de vidas (HTTP ${response.status}).`,
        erros: corpo.erros ?? corpo.problemas,
        solicitacaoId: corpo.solicitacaoId,
        statusAtual: corpo.statusAtual,
        retentavel: corpo.retentavel,
        correlationId: typeof corpo.correlationId === 'string' ? corpo.correlationId : undefined,
        retryAfterS: typeof corpo.retryAfterS === 'number' ? corpo.retryAfterS : undefined,
      });
    }
    return corpo.data as T;
  }

  meuPapel = () => this.chamar<ListaVidasPapelInfo>('meu-papel');

  listarEmpresas = (filtro: { busca?: string; soCriadasPeloCrm?: boolean; limite?: number } = {}) =>
    this.chamar<ListaVidasEmpresas>('listar-empresas', { ...filtro });

  listarVidas = (input: { empresaPublicId: string }) =>
    this.chamar<ListaVidasVidas>('listar-vidas', { empresaPublicId: input.empresaPublicId });

  /** Todas as empresas, com contagens (sem dado pessoal). */
  visaoGeral = (input: { incluirInativas?: boolean } = {}) =>
    this.chamar<ListaVidasVisaoGeral>('visao-geral', input.incluirInativas ? { incluirInativas: true } : {});

  /** PII completa de UMA vida (CPF, nascimento, mãe). Uso: preencher a edição
   *  e conferir um conflito. O servidor registra o acesso. */
  obterVida = (input: { empresaPublicId: string; seatPublicId: string }) =>
    this.chamar<{ vida: VidaCompletaDTO }>('obter-vida', {
      empresaPublicId: input.empresaPublicId, seatPublicId: input.seatPublicId,
    });

  listarSolicitacoes = (filtro: FiltroSolicitacoes = {}) =>
    this.chamar<{ solicitacoes: SolicitacaoDTO[]; servidorAgora: string }>('listar-solicitacoes', { ...filtro });

  obterSolicitacao = (input: { id: string }) =>
    this.chamar<{ solicitacao: SolicitacaoDTO; eventos: EventoSolicitacao[] }>('obter-solicitacao', { id: input.id });

  criar = (input: NovaSolicitacao) =>
    this.chamar<{ solicitacao: SolicitacaoDTO; replay: boolean }>('criar', { ...input });

  aprovar = (input: { id: string }) => this.chamar<RespostaAplicacao>('aprovar', { id: input.id });

  retomar = (input: { id: string }) => this.chamar<RespostaAplicacao>('retomar', { id: input.id });

  rejeitar = (input: { id: string; motivo: string; aceitarIncerteza?: boolean }) =>
    this.chamar<{ solicitacao: SolicitacaoDTO }>('rejeitar', {
      id: input.id,
      motivo: input.motivo,
      ...(input.aceitarIncerteza ? { aceitarIncerteza: true } : {}),
    });

  cancelar = (input: { id: string }) =>
    this.chamar<{ solicitacao: SolicitacaoDTO }>('cancelar', { id: input.id });

  // Controle de adicionais (F1: só no CRM, edição direta por cs/admin). O
  // `versao` é o CAS: null = ainda não há linha; divergência volta 409
  // `versao_desatualizada`.
  salvarAdicionalVida = (input: SalvarAdicionalVidaInput) =>
    this.chamar<{ controle: ControleAdicionalDTO }>('salvar-adicional-vida', {
      empresaPublicId: input.empresaPublicId, seatPublicId: input.seatPublicId,
      adicional: input.adicional, status: input.status, versao: input.versao,
    });

  liberarZenklub = (input: LiberarZenklubInput) =>
    this.chamar<{ controle: ControleAdicionalDTO }>('liberar-zenklub', {
      empresaPublicId: input.empresaPublicId, seatPublicId: input.seatPublicId, versao: input.versao,
      pedidoEm: input.pedidoEm, gestorRhNome: input.gestorRhNome,
      ...(input.liberadoEm ? { liberadoEm: input.liberadoEm } : {}),
    });

  salvarBonificacao = (input: SalvarBonificacaoInput) =>
    this.chamar<{ bonificacao: BonificacaoEmpresa }>('salvar-bonificacao', {
      empresaPublicId: input.empresaPublicId, adicional: input.adicional, bonificado: input.bonificado,
      ...(input.bonificado ? { inicio: input.inicio ?? null, fim: input.fim ?? null } : {}),
      versao: input.versao,
    });
}
